import React, { useState, useEffect, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { FaThumbsUp, FaTools, FaInfoCircle, FaArrowLeft } from "react-icons/fa";
import AgentViewTiles from "./AgentViewTiles";
import Grid from "./MainGrid";
import AgentViewFilter from "./AgentViewFilter";
import AgentViewCharts from "./AgentViewCharts";
import Tools from "../utils/Tools";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { useFilter } from "../contexts/FilterContext";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import "tippy.js/animations/shift-away.css";

const AIRecommendations = ({ recommendations }) => {
  // Function to format the tooltip content for performance reasons
  const formatPerformanceTooltip = (contactId) => {
    const performanceInfo = recommendations.performanceReasons?.find(
      (item) => item.contact_id === contactId
    );

    if (!performanceInfo) {
      return (
        <div className="p-3 max-w-md">
          <div className="font-bold mb-2 text-lg border-b pb-1">
            Contact Details
          </div>
          <div className="text-center mt-2">
            No performance information available for this contact.
          </div>
        </div>
      );
    }

    return (
      <div className="mt-1 text-white">
        {performanceInfo.agent_performance_reason}
      </div>
    );
  };

  // Function to render example call links as sub-bullet points with tooltips
  const renderExampleCallLinks = (exampleContactIds) => {
    if (!exampleContactIds || exampleContactIds.length === 0) return null;

    return (
      <ul className="list-[circle] ml-6 mt-1">
        {exampleContactIds.map((id, index) => (
          <li key={id} className="text-sm text-gray-600">
            Example call {" ("}
            <Tippy
              content={formatPerformanceTooltip(id)}
              placement="top"
              arrow={true}
              duration={[200, 200]}
              animation="shift-away"
              className="custom-tooltip"
              appendTo={document.body}
            >
              <a
                href={`${window.location.origin}/contact/${encodeURIComponent(
                  id
                )}`}
                className="text-blue-500 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {id}
              </a>
            </Tippy>
            {")"}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="bg-white rounded-lg p-8 mb-8 mx-auto container">
      <h3 className="text-xl font-semibold mb-4 flex items-center text-blue-600">
        AI Agent Training Recommendations
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-8">
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-xl font-semibold mb-4 flex items-center text-blue-600">
            <FaInfoCircle className="mr-2" /> Summary
          </h3>
          {recommendations.summary ? (
            <p className="text-gray-700">{recommendations.summary}</p>
          ) : (
            <p className="text-gray-700">No summary given.</p>
          )}
        </div>
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-xl font-semibold mb-4 flex items-center text-green-600">
            <FaThumbsUp className="mr-2" /> Strengths
          </h3>
          {recommendations.strengths && recommendations.strengths.length > 0 ? (
            <ul className="list-disc pl-5 text-gray-700 space-y-2">
              {recommendations.strengths.map((strength, index) => (
                <li key={`strength-${index}`}>
                  {strength.text}
                  {renderExampleCallLinks(strength.exampleContactIds)}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-700">No strengths listed.</p>
          )}
        </div>
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-xl font-semibold mb-4 flex items-center text-orange-600">
            <FaTools className="mr-2" /> Development Areas
          </h3>
          {recommendations.developmentAreas &&
          recommendations.developmentAreas.length > 0 ? (
            <ul className="list-disc pl-5 text-gray-700 space-y-2">
              {recommendations.developmentAreas.map((area, index) => (
                <li key={`area-${index}`}>
                  {area.text}
                  {renderExampleCallLinks(area.exampleContactIds)}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-700">No development areas listed.</p>
          )}
        </div>
      </div>
    </div>
  );
};

const AIGeneratingAnimation = () => {
  return (
    <div className="ai-generating-container">
      <h2 className="ai-generating-text">
        AI Generating Training Recommendations
      </h2>
      <div className="ai-generating-dots">
        <span className="dot"></span>
        <span className="dot"></span>
        <span className="dot"></span>
      </div>
    </div>
  );
};

function AgentView() {
  const { agentName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { filters, setFilters } = useFilter();
  const [agent, setAgent] = useState(null);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [showTrends, setShowTrends] = useState(false);
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [isLoadingRecommendation, setIsLoadingRecommendation] = useState(false);
  const [isRecommendationRequested, setIsRecommendationRequested] =
    useState(false);
  const [sortModel, setSortModel] = useState([]);

  function formatDateToYYYYMMDD(date) {
    const d = new Date(date);
    let month = "" + (d.getMonth() + 1);
    let day = "" + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    return [year, month, day].join("-");
  }

  const canGoBack = window.history.length > 1;

  const handleGoBack = () => {
    navigate(location.state?.referrer || "/agents", {
      state: { filters: filters },
    });
  };

  const handleFilterChange = useCallback(
    (newFilters) => {
      setFilters(newFilters);
      setCurrentPage(1); // Reset to first page on filter change
    },
    [setFilters]
  );

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleSortChange = useCallback((newSortModel) => {
    setSortModel(newSortModel);
  }, []);

  const handleAiReview = useCallback(() => {
    setIsLoadingRecommendation(true);
    setIsRecommendationRequested(true);
    Tools.sendRequest("get-agent-recommendations", {
      agent: agentName,
      dateRange: filters.dateRange.value,
    }).then((response) => {
      console.log("Got response", response);
      setAiRecommendation(response);
      setIsLoadingRecommendation(false);
    });
  }, [agentName, filters.dateRange]);

  useEffect(() => {
    setLoading(true);
    setLoadingSummary(true);
    const queryParams = new URLSearchParams();

    queryParams.append("agent", agentName);

    if (filters.queue && filters.queue.length > 0) {
      filters.queue.forEach((queue) => {
        queryParams.append("queue", queue.value);
      });
    }
    if (filters.initiationMethod && filters.initiationMethod.length > 0) {
      filters.initiationMethod.forEach((initiationMethod) => {
        queryParams.append("initiationMethod", initiationMethod.value);
      });
    }
    if (filters.dateRange) {
      queryParams.append("dateRange", filters.dateRange.value);
    }
    if (!filters.excludeWeekends) {
      queryParams.append("excludeWeekends", filters.excludeWeekends);
    }
    if (filters.dateStart) {
      queryParams.append("dateStart", formatDateToYYYYMMDD(filters.dateStart));
    }
    if (filters.dateEnd) {
      queryParams.append("dateEnd", formatDateToYYYYMMDD(filters.dateEnd));
    }

    Tools.sendRequest("get-agent-info", queryParams.toString()).then(
      (response) => {
        setAgent(response);
        setLoadingSummary(false);
      }
    );

    queryParams.append("page", currentPage);

    // sorting parameters
    if (sortModel && sortModel.length > 0) {
      sortModel.forEach((sortItem) => {
        queryParams.append("sortBy", sortItem.colId);
        queryParams.append("sortOrder", sortItem.sort);
      });
    }

    Tools.sendRequest("get-data", queryParams.toString()).then((response) => {
      setRowData(response.data);
      setTotalRecords(response.totalRecords);
      setTotalPages(response.totalPages);
      setCurrentPage(response.currentPage);
      setLoading(false); // Data fetching is complete
    });
  }, [currentPage, filters, agentName, sortModel]);

  return (
    <div>
      <div className="flex justify-between items-center mb-4 max-w-screen-xl mx-auto">
        <div className="w-1/3">
          {canGoBack && (
            <button
              onClick={handleGoBack}
              className="text-sm text-gray-600 hover:text-gray-800 flex items-center"
            >
              <FaArrowLeft className="mr-1" /> Back
            </button>
          )}
        </div>
        <h2 className="text-2xl font-bold text-center w-1/3">{agentName}</h2>
        <div className="w-1/3 flex justify-end mr-10">
          <button
            className={`${
              isRecommendationRequested
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            } text-white font-bold py-2 px-4 rounded`}
            onClick={handleAiReview}
            disabled={isRecommendationRequested}
          >
            AI Coaching Recommendations
          </button>
        </div>
      </div>
      <TransitionGroup>
        {isLoadingRecommendation && (
          <CSSTransition key="loading" classNames="fade" timeout={500}>
            <AIGeneratingAnimation />
          </CSSTransition>
        )}
        {aiRecommendation && (
          <CSSTransition key="recommendations" classNames="fade" timeout={500}>
            <AIRecommendations recommendations={aiRecommendation} />
          </CSSTransition>
        )}
      </TransitionGroup>
      <div style={{ 'maxWidth': '1780px' }} className="container mx-auto p-4">
        <AgentViewFilter
          filters={filters}
          setFilters={handleFilterChange}
          updateShowTrends={setShowTrends}
          agent={agent}
        />
        {loading || loadingSummary ? (
          <div className="flex justify-center items-center h-64">
            <div className="loader"></div>
          </div>
        ) : (
          <>
            <AgentViewTiles agent={agent} showTrends={showTrends} />
            <AgentViewCharts agent={agent} showTrends={showTrends} />
            <Grid
              data={rowData}
              currentPage={currentPage}
              totalPages={totalPages}
              totalRecs={totalRecords}
              onPageChange={handlePageChange}
              onSortChange={handleSortChange}
              filters={filters}
              sortModel={sortModel} // Pass sortModel to Grid
              isQualityMangament={false}
              selectedAgent={agentName}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default AgentView;
